<script setup lang="ts">
import { ref, defineEmits } from "vue";
import type { Base64File, UploadFile } from "@/struct/FileTypes";
import { useI18n } from "vue-i18n";

/* -------- emits -------- */
const emit = defineEmits<{
  (e: "update:files", files: Base64File[]): void;
}>();

/* -------- state -------- */
const files = ref<UploadFile[]>([]);
const isDragging = ref(false);
const error = ref<string | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const { t } = useI18n();

/* -------- icons -------- */
const DefaultIcon = {
  template: `<div class="icon-default">📎</div>`,
};

const DragIcon = {
  template: `<div class="icon-drag">➕</div>`,
};

/* -------- handlers -------- */
function openFileDialog() {
  fileInput.value?.click();
}

function onDragEnter() {
  isDragging.value = true;
}

function onDragLeave() {
  isDragging.value = false;
}

function onDrop(e: DragEvent) {
  isDragging.value = false;
  if (e.dataTransfer?.files) {
    handleFiles(Array.from(e.dataTransfer.files));
  }
}

function onFileSelect(e: Event) {
  const input = e.target as HTMLInputElement;
  if (input.files) {
    handleFiles(Array.from(input.files));
    input.value = "";
  }
}

/* -------- logic -------- */
async function handleFiles(selected: File[]) {
  error.value = null;

  for (const file of selected) {
    if (!isValid(file)) {
      error.value = t("base.drop.uploadError");
      continue;
    }

    const uploadFile: UploadFile = {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: "uploading",
    };

    files.value.push(uploadFile);

    try {
      const base64 = await fileToBase64(file);

      const index = files.value.findIndex((f) => f.id === uploadFile.id);
      if (index !== -1) {
        files.value[index] = {
          ...files.value[index],
          base64,
          status: "success",
        };
      }

      emitFiles();
    } catch {
      updateFile(uploadFile.id, {
        status: "error",
      });
    }
  }
}

function emitFiles() {
  const result: Base64File[] = files.value
    .filter((f) => f.status === "success" && f.base64)
    .map((f) => ({
      id: f.id,
      name: f.name,
      type: f.type,
      size: f.size,
      base64: f.base64!,
    }));

  emit("update:files", result);
}

function updateFile(id: string, patch: Partial<UploadFile>) {
  const index = files.value.findIndex((f) => f.id === id);
  if (index !== -1) {
    files.value[index] = {
      ...files.value[index],
      ...patch,
    };
  }
}

function removeFile(id: string) {
  files.value = files.value.filter((f) => f.id !== id);
  emitFiles();
}

function isValid(file: File) {
  const allowed = ["application/pdf", "image/png", "image/jpeg"];
  const maxSize = 10 * 1024 * 1024;
  return allowed.includes(file.type) && file.size <= maxSize;
}

function formatSize(bytes: number) {
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject();
  });
}
</script>

<template>
  <div class="file-dropzone">
    <!-- DROP AREA -->
    <div
      class="drop-area"
      :class="{ 'is-dragging': isDragging }"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="openFileDialog"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        accept=".pdf,.png,.jpg,.jpeg"
        tabindex="-1"
        aria-hidden="true"
        @change="onFileSelect"
        style="
          position: absolute;
          width: 0;
          height: 0;
          opacity: 0;
          pointer-events: none;
        "
      />

      <div class="content">
        <component :is="isDragging ? DragIcon : DefaultIcon" class="icon" />

        <div class="title">{{ t("base.drop.title") }}</div>
        <div class="subtitle">{{ t("base.drop.subtitle") }}</div>
      </div>
    </div>

    <!-- FILE LIST -->
    <div v-if="files.length" class="file-list">
      <div
        v-for="file in files"
        :key="file.id"
        class="file-item"
        :class="file.status"
      >
        <div class="file-info">
          <span class="file-icon">PDF</span>

          <div class="meta">
            <div class="name">{{ file.name }}</div>
            <div class="size">{{ formatSize(file.size) }}</div>
          </div>
        </div>

        <div class="actions">
          <span v-if="file.status === 'uploading'" class="loader" />
          <button v-else class="remove" @click="removeFile(file.id)">✕</button>
        </div>
      </div>

      <p v-if="error" class="error">
        {{ error }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.file-dropzone {
  width: 100%;
}

/* DROP AREA */
.drop-area {
  border: 2px dashed #cfd3eb;
  border-radius: 14px;
  padding: 32px;
  cursor: pointer;
  transition: 0.2s;
  text-align: center;
}

.drop-area.is-dragging {
  border-color: #5b5be1;
  background: #f5f6ff;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.title {
  font-weight: 600;
  font-size: 18px;
}

.subtitle {
  font-size: 14px;
  color: #6b6f8d;
}

/* FILE LIST */
.file-list {
  margin-top: 16px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #e4e6f5;
  margin-bottom: 10px;
}

.file-item.error {
  border-color: #ff4d4f;
}

.file-info {
  display: flex;
  gap: 12px;
}

.file-icon {
  background: #eef0ff;
  padding: 8px;
  border-radius: 8px;
  font-weight: 600;
}

.meta .name {
  font-weight: 500;
}

.meta .size {
  font-size: 13px;
  color: #888;
}

.actions {
  display: flex;
  align-items: center;
}

.remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
}

/* loader */
.loader {
  width: 18px;
  height: 18px;
  border: 2px solid #ccc;
  border-top-color: #5b5be1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error {
  margin-top: 8px;
  color: #ff4d4f;
  font-size: 14px;
}
</style>
